// Console Banner Configuration for PadelMaster
// This file contains version info and console styling

import {
  getCurrentEnvironment,
  isOfficialDomain,
  validateDomainSecurity
} from '../config/domains';

export const VERSION_INFO = {
  name: 'PadelMaster',
  version: '3.0.1',
  build: '20240731-214430',
  date: '31/07/2024',
  frontend: '3.0.1',
  backend: '3.0.0',
  author: 'Marcio Miguel & AI Assistant'
};

export const CONSOLE_STYLES = {
  banner: `
    background: linear-gradient(45deg, #1e40af, #3b82f6);
    color: white;
    font-weight: bold;
    font-size: 16px;
    padding: 10px 20px;
    border-radius: 8px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  `,
  title: `
    color: #1e40af;
    font-weight: bold;
    font-size: 24px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
  `,
  info: `
    color: #374151;
    font-size: 14px;
    font-family: 'Courier New', monospace;
  `,
  version: `
    color: #059669;
    font-weight: bold;
    font-size: 16px;
  `,
  warning: `
    color: #dc2626;
    font-weight: bold;
    font-size: 14px;
  `,
  success: `
    color: #16a34a;
    font-weight: bold;
    font-size: 14px;
  `
};

export const createConsoleBanner = () => {
  const currentEnvironment = getCurrentEnvironment();
  const securityCheck = validateDomainSecurity();
  
  // Clear console first (only in production)
  if (currentEnvironment === 'production') {
    console.clear();
  }

  // Simplified banner - much less verbose
  console.log(
    `%c🏆 PadelMaster v${VERSION_INFO.version} | ${currentEnvironment === 'development' ? 'Dev' : 'Prod'}`,
    CONSOLE_STYLES.banner
  );

  // Only show critical security warnings
  if (!isOfficialDomain()) {
    console.log(`%c⚠️  Atenção: Domínio não oficial`, CONSOLE_STYLES.warning);
  } else if (securityCheck.warnings.length > 0) {
    console.log(`%c⚠️  Avisos de Segurança: ${securityCheck.warnings.length}`, CONSOLE_STYLES.warning);
  }

  // Show only essential info for developers in dev mode
  if (currentEnvironment === 'development') {
    console.log(`%c🔧 Dev: React+Vite+Django | Build: ${VERSION_INFO.build}`, CONSOLE_STYLES.info);
    
    // Performance info (if available)
    if (performance && performance.timing) {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      if (loadTime > 0 && loadTime > 2000) { // Only show if slow
        console.log(`%c⚡ Load: ${loadTime}ms`, CONSOLE_STYLES.warning);
      }
    }
  }
};

// Simplified developer info - only show on demand
export const displayDeveloperInfo = () => {
  console.group('%c🔧 Dev Info', CONSOLE_STYLES.version);
  console.log('window.PadelMaster.showVersion() | .showStats() | .clearData()');
  console.groupEnd();
};

// Global object for developers
declare global {
  interface Window {
    PadelMaster: {
      version: typeof VERSION_INFO;
      showVersion: () => void;
      showStats: () => void;
      clearData: () => void;
      debug: boolean;
      console: {
        banner: () => void;
        developerInfo: () => void;
      };
    };
  }
}

export const setupGlobalObject = () => {
  window.PadelMaster = {
    version: VERSION_INFO,
    debug: false,
    showVersion: () => {
      console.table(VERSION_INFO);
    },
    showStats: () => {
      const stats = {
        'Local Storage': `${Object.keys(localStorage).length} itens`,
        'Session Storage': `${Object.keys(sessionStorage).length} itens`,
        'Cookies': document.cookie.split(';').length,
        'User Agent': navigator.userAgent,
        'Resolução': `${screen.width}x${screen.height}`,
        'Viewport': `${window.innerWidth}x${window.innerHeight}`
      };
      console.table(stats);
    },
    clearData: () => {
      if (confirm('⚠️ Limpar todos os dados locais? Esta ação não pode ser desfeita.')) {
        localStorage.clear();
        sessionStorage.clear();
        console.log('%c✅ Dados locais limpos com sucesso!', CONSOLE_STYLES.success);
        window.location.reload();
      }
    },
    console: {
      banner: createConsoleBanner,
      developerInfo: displayDeveloperInfo
    }
  };
};
